import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useFindUpcomingEvents } from "../../api/queries/useFindUpcomingEvents";
import { isLoggedIn } from "../../auth/authStorage";
import {
  clearExplorerJoinForceSignup,
  markExplorerJoinSelectedWhileAnonymous,
  setExplorerContext,
  type ExplorerFlow,
} from "../../explorer/explorerSession";
import {
  getEventCardPhase,
  isEventDateStillActive,
  isPastEventDateEnd,
  parseEventDateAsLocalDay,
} from "../../utils/eventDateUtils";
import { Dropdown } from "react-bootstrap";
import { ClampedTextWithTooltip } from "../events/ClampedTextWithTooltip";
import {
  FilterDropdown,
  SearchableFilterDropdown,
} from "../events/EventFilterDropdowns";
import {
  EMPTY_FILTERS,
  EVENT_CARD_IMAGES,
  META_CLASS,
  META_STYLE,
  PHASE_CARD_CLASS,
  SLOT,
  eventMatchesListFilters,
} from "../events/eventsListShared";

const FEATURE_ICON_SIZE = { width: 48, height: 48 };
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function formatEventDateLabel(eventDate: string, locale: string) {
  return parseEventDateAsLocalDay(eventDate).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toLocalDateKey(day: Date) {
  const y = day.getFullYear();
  const m = String(day.getMonth() + 1).padStart(2, "0");
  const d = String(day.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Generic Explorer entry URL:
 * - /explorer/join (landing with active events)
 * - /explorer/join?eventId=123&flow=event (existing redirect behavior)
 */
function ExplorerJoin() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage || i18n.language;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get("eventId");
  const flow = (searchParams.get("flow") || "event") as ExplorerFlow;
  const {
    data: eventsResponse,
    isPending: isLoadingEvents,
    error: eventsError,
  } = useFindUpcomingEvents({ pageSize: 100, page: 1, enabled: !eventId });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [cityMenuSearch, setCityMenuSearch] = useState("");
  const [countryMenuSearch, setCountryMenuSearch] = useState("");
  const [calendarYear, setCalendarYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [calendarMonth, setCalendarMonth] = useState(() =>
    new Date().getMonth(),
  );
  /** Dev Strict Mode runs effects twice; avoid double redirect / double consume of session intent. */
  const joinEventRedirectHandledRef = useRef<string | null>(null);
  const calendarMonthInitializedRef = useRef(false);

  const activeEvents = useMemo(() => {
    if (!eventsResponse?.data?.length) return [];
    return eventsResponse.data
      .filter(
        (event) =>
          isEventDateStillActive(event.eventDate) &&
          !isPastEventDateEnd(event.eventDate),
      )
      .sort(
        (a, b) =>
          parseEventDateAsLocalDay(a.eventDate).getTime() -
          parseEventDateAsLocalDay(b.eventDate).getTime(),
      );
  }, [eventsResponse?.data]);

  const { availableCities, availableCountries, availableDates, eventDateKeyToLabel } =
    useMemo(() => {
      const cities = new Set<string>();
      const countries = new Set<string>();
      const dates = new Map<string, number>();
      const keyToLabel = new Map<string, string>();

      for (const event of activeEvents) {
        const loc = event.location?.trim();
        if (loc) {
          const first = loc.indexOf(",");
          const last = loc.lastIndexOf(",");
          cities.add(first >= 0 ? loc.slice(0, first).trim() || loc : loc);
          if (last >= 0) {
            const country = loc.slice(last + 1).trim();
            if (country) countries.add(country);
          }
        }
        const day = parseEventDateAsLocalDay(event.eventDate);
        const label = formatEventDateLabel(event.eventDate, dateLocale);
        if (!dates.has(label)) dates.set(label, day.getTime());
        keyToLabel.set(toLocalDateKey(day), label);
      }

      const byName = (a: string, b: string) => a.localeCompare(b);
      return {
        availableCities: Array.from(cities).sort(byName),
        availableCountries: Array.from(countries).sort(byName),
        availableDates: Array.from(dates.entries())
          .sort((a, b) => a[1] - b[1])
          .map(([label]) => label),
        eventDateKeyToLabel: keyToLabel,
      };
    }, [activeEvents, dateLocale]);

  useEffect(() => {
    if (calendarMonthInitializedRef.current || activeEvents.length === 0) return;
    const firstDay = parseEventDateAsLocalDay(activeEvents[0].eventDate);
    setCalendarYear(firstDay.getFullYear());
    setCalendarMonth(firstDay.getMonth());
    calendarMonthInitializedRef.current = true;
  }, [activeEvents]);

  const calendarCells = useMemo(() => {
    const firstOfMonth = new Date(calendarYear, calendarMonth, 1);
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const leadingBlanks = firstOfMonth.getDay();
    const cells: Array<{ day: number | null; key: string | null; label: string | null }> =
      [];

    for (let i = 0; i < leadingBlanks; i += 1) {
      cells.push({ day: null, key: null, label: null });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(calendarYear, calendarMonth, day);
      const key = toLocalDateKey(date);
      cells.push({
        day,
        key,
        label: eventDateKeyToLabel.get(key) ?? null,
      });
    }
    return cells;
  }, [calendarYear, calendarMonth, eventDateKeyToLabel]);

  const calendarMonthLabel = useMemo(
    () =>
      new Date(calendarYear, calendarMonth, 1).toLocaleDateString(dateLocale, {
        month: "long",
        year: "numeric",
      }),
    [calendarYear, calendarMonth, dateLocale],
  );

  const shiftCalendarMonth = (delta: number) => {
    const next = new Date(calendarYear, calendarMonth + delta, 1);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const listFilters = {
      ...EMPTY_FILTERS,
      city: filterCity,
      country: filterCountry,
    };

    return activeEvents.filter((event) => {
      if (q) {
        const haystack =
          `${event.name} ${event.description ?? ""} ${event.location ?? ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (!eventMatchesListFilters(event, listFilters)) return false;
      if (
        filterDate &&
        formatEventDateLabel(event.eventDate, dateLocale) !== filterDate
      ) {
        return false;
      }
      return true;
    });
  }, [
    activeEvents,
    searchTerm,
    filterCity,
    filterCountry,
    filterDate,
    dateLocale,
  ]);

  const hasActiveFilter =
    searchTerm.trim().length > 0 ||
    Boolean(filterCity) ||
    Boolean(filterCountry) ||
    Boolean(filterDate);

  const locationFilters = [
    {
      id: "explorer-city-filter-toggle",
      value: filterCity,
      options: availableCities,
      menuSearch: cityMenuSearch,
      onMenuSearch: setCityMenuSearch,
      onSelect: setFilterCity,
      icon: "bi-geo-alt",
      label: t("events.filterCity"),
      placeholder: t("events.filterCityPlaceholder"),
      clearLabel: t("events.clearCityFilter"),
      emptyLabel: t("events.noCitiesAvailable"),
    },
    {
      id: "explorer-country-filter-toggle",
      value: filterCountry,
      options: availableCountries,
      menuSearch: countryMenuSearch,
      onMenuSearch: setCountryMenuSearch,
      onSelect: setFilterCountry,
      icon: "bi-globe2",
      label: t("events.filterCountry"),
      placeholder: t("events.filterCountryPlaceholder"),
      clearLabel: t("events.clearCountryFilter"),
      emptyLabel: t("events.noCountriesAvailable"),
    },
  ];

  useEffect(() => {
    if (!eventId) return;
    const dedupeKey = `${eventId}:${flow}`;
    if (joinEventRedirectHandledRef.current === dedupeKey) return;
    joinEventRedirectHandledRef.current = dedupeKey;

    setExplorerContext(eventId, flow);

    const q = new URLSearchParams();
    if (eventId) q.set("eventId", eventId);
    const entrySource =
      flow === "mentor"
        ? "mentorship"
        : flow === "mentee"
          ? "mentee"
          : flow === "venture"
            ? "venture"
            : "event";
    q.set("entrySource", entrySource);
    navigate(`/explorer/signup?${q.toString()}`, { replace: true });
  }, [eventId, flow, navigate]);

  const handleSelectEvent = (selectedEventId: number, selectedEventCode?: string | null) => {
    if (selectedEventCode?.trim()) {
      const safeCode = encodeURIComponent(selectedEventCode.trim());
      navigate(`/explorer/signup/${safeCode}?entrySource=event`);
      return;
    }
    if (isLoggedIn()) clearExplorerJoinForceSignup();
    else markExplorerJoinSelectedWhileAnonymous(String(selectedEventId));
    navigate(`/explorer/join?eventId=${selectedEventId}&flow=event`);
  };

  if (eventId) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="explorer-wizard-root explorer-event-landing-root position-relative bg-white">
      <div className="explorer-wizard-backdrop" aria-hidden />
      <div className="position-relative z-1 w-100 mx-auto">
        <div className="explorer-wizard-card explorer-event-landing-card w-100 border-0 shadow-none bg-transparent rounded-0 overflow-visible">
          <div className="explorer-wizard-body explorer-event-landing-body">
            <header className="explorer-landing-site-header d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div className="explorer-landing-brand d-flex align-items-center gap-2">
                <img src="/connect-logo.png" alt="SMBF logo" />
              </div>
             
              <div className="explorer-landing-header-actions d-inline-flex align-items-center gap-2">
              
                <a
                  href="#explorer-events"
                  className="btn bg-project-gradient border-0 rounded-3 px-5 py-2 fw-semibold text-white"
                >
                  Join Now
                </a>
              </div>
            </header>
            <section className="row g-3 g-lg-4 mb-3">
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-people fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Community Reach</div>
                        <div className="fw-bold mb-1">Growing Community</div>
                        <p className="text-muted small mb-0">
                          Founders, mentors, and operators actively sharing opportunities.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-chat-dots fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Live Conversations</div>
                        <div className="fw-bold mb-1">Weekly Networking</div>
                        <p className="text-muted small mb-0">
                          Curated sessions that help members connect and collaborate quickly.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-globe2 fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Global Visibility</div>
                        <div className="fw-bold mb-1">12+ Countries</div>
                        <p className="text-muted small mb-0">
                          Cross-border exposure through trusted Muslim business circles.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <div className="mb-3" id="explorer-events">
              <h2 className="fs-3 fw-bold text-dark mb-2 lh-sm">
                Choose your event to continue
              </h2>
              <p className="text-secondary mb-4">
                Pick an active event below and proceed directly to signup.
              </p>
              {!isLoadingEvents && !eventsError && activeEvents.length > 0 ? (
                <div className="explorer-event-landing-toolbar d-flex flex-column gap-2 align-items-stretch">
                  <div className="row g-2 g-lg-3 align-items-center w-100">
                    <div className="col-12 col-sm-6 col-lg-3">
                      <div className="input-group input-group-sm border border-secondary-subtle rounded-3 shadow-sm overflow-hidden bg-white">
                        <span className="input-group-text bg-white border-0 ps-3 pe-2 text-secondary">
                          <i className="bi bi-search" aria-hidden />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-sm border-0 bg-white py-2 ps-1 text-secondary small"
                          placeholder="Search events by name, description, or location..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          aria-label="Search active events"
                        />
                      </div>
                    </div>
                    {locationFilters.map((f) => (
                      <div key={f.id} className="col-12 col-sm-6 col-lg-3">
                        <SearchableFilterDropdown
                          id={f.id}
                          value={f.value}
                          options={f.options}
                          menuSearch={f.menuSearch}
                          onMenuSearch={f.onMenuSearch}
                          onSelect={(v) => {
                            f.onSelect(v);
                            f.onMenuSearch("");
                          }}
                          onClear={() => {
                            f.onSelect("");
                            f.onMenuSearch("");
                          }}
                          icon={f.icon}
                          label={f.label}
                          placeholder={f.placeholder}
                          clearLabel={f.clearLabel}
                          emptyLabel={f.emptyLabel}
                          style={{ width: "100%", minWidth: 0 }}
                        />
                      </div>
                    ))}
                    <div className="col-12 col-sm-6 col-lg-3">
                      <FilterDropdown
                        id="explorer-date-filter-toggle"
                        active={Boolean(filterDate)}
                        label={filterDate || t("events.eventDate")}
                        ariaLabel={t("events.eventDate")}
                        icon="bi-calendar3"
                        style={{ width: "100%", minWidth: 0 }}
                      >
                        <div
                          className="px-1"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              aria-label="Previous month"
                              onClick={() => shiftCalendarMonth(-1)}
                            >
                              <i className="bi bi-chevron-left" aria-hidden />
                            </button>
                            <span className="fw-semibold small text-center text-truncate">
                              {calendarMonthLabel}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              aria-label="Next month"
                              onClick={() => shiftCalendarMonth(1)}
                            >
                              <i className="bi bi-chevron-right" aria-hidden />
                            </button>
                          </div>
                          {availableDates.length === 0 ? (
                            <div className="text-muted small px-2 py-1">
                              No dates available
                            </div>
                          ) : (
                            <table className="table table-borderless table-sm mb-2 text-center align-middle">
                              <thead>
                                <tr>
                                  {WEEKDAY_LABELS.map((weekday) => (
                                    <th
                                      key={weekday}
                                      scope="col"
                                      className="fw-semibold text-secondary small p-1"
                                    >
                                      {weekday}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {Array.from(
                                  {
                                    length: Math.ceil(calendarCells.length / 7),
                                  },
                                  (_, weekIndex) => (
                                    <tr key={`week-${weekIndex}`}>
                                      {calendarCells
                                        .slice(
                                          weekIndex * 7,
                                          weekIndex * 7 + 7,
                                        )
                                        .map((cell, dayIndex) => {
                                          if (cell.day == null) {
                                            return (
                                              <td
                                                key={`blank-${weekIndex}-${dayIndex}`}
                                                className="p-1"
                                              />
                                            );
                                          }
                                          const isSelectable = Boolean(
                                            cell.label,
                                          );
                                          const isSelected =
                                            Boolean(cell.label) &&
                                            cell.label === filterDate;
                                          return (
                                            <td
                                              key={
                                                cell.key ??
                                                `${weekIndex}-${dayIndex}`
                                              }
                                              className="p-1"
                                            >
                                              <button
                                                type="button"
                                                className={`btn btn-sm w-100 px-0 ${
                                                  isSelected
                                                    ? "btn-primary"
                                                    : isSelectable
                                                      ? "btn-outline-primary"
                                                      : "btn-light text-muted"
                                                }`}
                                                disabled={!isSelectable}
                                                aria-pressed={isSelected}
                                                aria-label={
                                                  cell.label ??
                                                  `Day ${cell.day}`
                                                }
                                                onClick={() => {
                                                  if (cell.label) {
                                                    setFilterDate(cell.label);
                                                  }
                                                }}
                                              >
                                                {cell.day}
                                              </button>
                                            </td>
                                          );
                                        })}
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          )}
                          {filterDate ? (
                            <Dropdown.Item
                              as="button"
                              type="button"
                              className="rounded-3 text-danger small"
                              onClick={() => setFilterDate("")}
                            >
                              Clear date
                            </Dropdown.Item>
                          ) : null}
                        </div>
                      </FilterDropdown>
                    </div>
                  </div>
                  {hasActiveFilter ? (
                    <div className="explorer-event-toolbar-note small text-secondary border rounded-pill px-2 py-1 bg-white bg-opacity-75">
                      Showing {filteredEvents.length} of {activeEvents.length}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {isLoadingEvents ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading active events…</span>
                </div>
              </div>
            ) : eventsError ? (
              <div className="alert alert-danger mb-0" role="alert">
                We could not load active events right now. Please try again shortly.
              </div>
            ) : activeEvents.length === 0 ? (
              <div className="alert alert-info mb-0" role="alert">
                There are no active events at the moment.
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="alert alert-secondary mb-0" role="alert">
                No events match your search or filters. Try different criteria.
              </div>
            ) : (
              <div className="row g-3 g-lg-4">
                {filteredEvents.map((event) => {
                  const phase = getEventCardPhase(event.eventDate);
                  const phaseLabel =
                    phase === "today"
                      ? t("events.today")
                      : phase === "upcoming"
                        ? t("events.upcoming")
                        : t("events.ended");
                  const ribbonIcon =
                    phase === "today"
                      ? "bi-calendar-event-fill"
                      : phase === "upcoming"
                        ? "bi-clock-fill"
                        : "bi-x-circle-fill";
                  const ribbonTone =
                    phase === "today"
                      ? "bg-success text-white"
                      : phase === "upcoming"
                        ? "bg-project-gradient text-white"
                        : "bg-danger text-white";
                  const title = event.name || "";
                  const description = event.description || "Event";
                  const imageUrl =
                    event.bannerImage ||
                    EVENT_CARD_IMAGES[event.id % EVENT_CARD_IMAGES.length];

                  return (
                    <div className="col-md-4" key={event.id}>
                      <article
                        className={`${PHASE_CARD_CLASS[phase]} h-100 d-flex flex-column`}
                      >
                        <div className="p-2 pb-0">
                          <div className="position-relative rounded-3 overflow-hidden">
                            <div className="ratio ratio-16x9">
                              <img
                                src={imageUrl}
                                alt={title}
                                className="object-fit-cover w-100 h-100"
                              />
                            </div>
                            <span className="position-absolute top-0 start-0 m-2 badge bg-white text-dark border border-white border-2 px-2 py-1 fw-normal small">
                              <i className="bi bi-calendar3 me-1" aria-hidden />
                              {formatEventDateLabel(event.eventDate, dateLocale)}
                            </span>
                            <div
                              className="position-absolute top-0 end-0 overflow-hidden"
                              style={{
                                width: 88,
                                height: 88,
                                pointerEvents: "none",
                              }}
                              aria-label={phaseLabel}
                            >
                              <span
                                className={`position-absolute ${ribbonTone} small fw-semibold text-uppercase d-inline-flex align-items-center justify-content-center gap-1 shadow-sm`}
                                style={{
                                  width: 120,
                                  top: 18,
                                  right: -28,
                                  transform: "rotate(45deg)",
                                  padding: "0.2rem 0",
                                  fontSize: "0.65rem",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                <i className={`bi ${ribbonIcon}`} aria-hidden />
                                {phaseLabel}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="card-body px-3 py-2 d-flex flex-column small flex-grow-1">
                          <div className="mb-2" style={SLOT.title}>
                            <ClampedTextWithTooltip
                              id={`explorer-event-title-${event.id}`}
                              text={title}
                              className="fw-bold text-dark mb-0"
                              lines={1}
                            />
                          </div>
                          <div className="mb-2" style={SLOT.desc}>
                            <ClampedTextWithTooltip
                              id={`explorer-event-desc-${event.id}`}
                              text={description}
                              className={META_CLASS}
                              style={META_STYLE}
                              lines={2}
                            />
                          </div>

                          <div
                            className={`${META_CLASS} mb-3 d-flex align-items-center min-w-0`}
                            style={SLOT.location}
                          >
                            <i
                              className="bi bi-geo-alt me-1 flex-shrink-0"
                              aria-hidden
                            />
                            <ClampedTextWithTooltip
                              id={`explorer-event-location-${event.id}`}
                              text={event.location || "TBC"}
                              className={`${META_CLASS} min-w-0 flex-grow-1`}
                              style={META_STYLE}
                              lines={1}
                            />
                          </div>

                          <button
                            type="button"
                            className="btn explorer-wizard-btn explorer-event-card-cta bg-project-gradient border-0 rounded-3 w-100 mt-auto"
                            onClick={() =>
                              handleSelectEvent(event.id, event.eventCode)
                            }
                          >
                            Register for the event
                            <i className="bi bi-arrow-right ms-1" aria-hidden />
                          </button>
                        </div>
                      </article>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExplorerJoin;
